/*
 * author: Michael Bauer (Geoplex)
 * email:  michael.bauer@geoplex.com.au
 *
 */

import TimeExtent from '@arcgis/core/TimeExtent';
import TimeInfo from "@arcgis/core/layers/support/TimeInfo";
import ImageryLayer from '@arcgis/core/layers/ImageryLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import WMTSLayer from '@arcgis/core/layers/WMTSLayer';
import WCSLayer from '@arcgis/core/layers/WCSLayer';
import ESRILayer from '@arcgis/core/layers/Layer';
import MosaicRule from '@arcgis/core/layers/support/MosaicRule';
import * as imageService from "@arcgis/core/rest/imageService";
import ImageIdentifyParameters from "@arcgis/core/rest/support/ImageIdentifyParameters";
import * as query from "@arcgis/core/rest/query";
import DimensionalDefinition from "@arcgis/core/layers/support/DimensionalDefinition";
import MultipartColorRamp from "@arcgis/core/rest/support/MultipartColorRamp";
import RasterStretchRenderer from "@arcgis/core/renderers/RasterStretchRenderer";
import * as watchUtils from '@arcgis/core/core/watchUtils';
import Extent from '@arcgis/core/geometry/Extent';
import Color from "@arcgis/core/Color";
import Query from '@arcgis/core/rest/support/Query';
import Basemap from "@arcgis/core/Basemap";
import Map from "@arcgis/core/Map";
import ESRIRequest from "@arcgis/core/request";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Graphic from "@arcgis/core/Graphic";

import { appConfig, showWarning } from "./main"
import { monthOfYear } from "./dates"
import { WidgetBuilderSettings } from './widgetbuilder'
import { 
    FIREWEATHER_SERVICES_URL, 
    GUARDIAN_SERVICES_URL, 
    GEOSERVER_URL, 
    AIG_GEOSERVER_URL, 
    INCIDENTS_URL,
    GIS_MAP_ARCGIS_SERVER
} from './env'


// @ts-ignore Imported by JS ENV
let fireweather_services_url = FIREWEATHER_SERVICES_URL + "/arcgis/rest/services/FireWeather"

// @ts-ignore Imported by JS ENV
let guardian_ref_url = GUARDIAN_SERVICES_URL + "/arcgis/rest/services/Reference/GuardianRef/MapServer"


export let tx10min2hours : TimeExtent = new TimeExtent({
    start: new Date("1900/01/01 00:00:00 UTC"),
    end: new Date("1911/01/01 00:00:00 UTC")
})

interface Layers {
    [key: string]: Layer
}

interface BaseMaps {
    [key: string]: Basemap
}

interface TimeMetadata {
    [key: string] : any
}

let layersCache: Layers | null = null

let customBaseMaps: BaseMaps | null = null

export let planetMosaics: string[][] = []

/**
 * A Class that manages layers and simplifies the loading and display of layer details
 */
export class Layer {
    
    esriLayer: ESRILayer | null = null
    esriLayerOptions: any = {}
    order: number = 5

    controls: WidgetBuilderSettings[] = []
    step: string = '1900'
    metadata: TimeMetadata = {}
    allowCaching: boolean = true //until refresh of page

    attachedToMap: boolean = false;

    private rndKey: string = Math.floor(Math.random()*10000).toString()

    private onSelectCallback: ((e?: any) => void) | null = null
    private onUpdateCallback: ((e?: any) => void) | null = null
    private onLoadCallback:   ((e?: any) => void) | null = null

    public id?: string
    public timeExtent?: TimeExtent
    public params: any = {} // Layer specific parameters, useful for pixel filters and state info
    public isContextual: boolean = false // Contextual Layers may be overlayed, with only ever one non-contextual layer
    public name?: string
    public layerType: string = "ImageryLayer"
    public url?: string
    public qryUrl?: string
    public desc: string = ""
    public warnings: string | null = null
    public warningShown: boolean = false
    public allowRefresh = true

    public boundaryLayerId: string | null = null;
    public layerId: string | null = null;

    public timeSteps: string[] | null = null;


    constructor(options: any) {

        this.applyConfig(options)

        if(!this.name){
            this.name = this.id;
        }

        if (this.url === undefined) {
            this.url = fireweather_services_url + "/" + this.id + "/ImageServer"
        }

        if(!this.qryUrl){
            this.qryUrl = this.url
        }

        if (this.isContextual && !options.order) {
            this.order = 100
        }
    }

    /**
     *  Used for setting presets and saved states
     */
    applyConfig(options: any, updateESRILayer: boolean = false) {
        for (let key in options) {
            this[key] = options[key]
        }
        if (updateESRILayer && this.esriLayer && appConfig.activeView) {
            if(this.esriLayerOptions.id === 'incidents') {
                // This is done to ensure that the graphics update correctly
                this.esriLayer.destroy();
                this.esriLayer = new FeatureLayer(this.esriLayerOptions);
                this.addLayerToMap(appConfig.activeView.map);
                
                // @ts-ignore
                this.esriLayer.refresh()
            } else {
                for (let key in this.esriLayerOptions) {
                    this.esriLayer[key] = this.esriLayerOptions[key];
                }
            }
        }
    }

    /**
     *  Will load the relevant ESRI JS API Layer based on the layerType value
     */
    initESRILayer(options?: any){
        if(!options){
            options = this.esriLayerOptions
        }

        if(options.url === undefined){
            options.url = this.url;
        }
        
        if(!options.id){
            options.id = this.id
        }
        
        if (this.layerType == "ImageryLayer") {
            if (!options.mosaicRule) {
                if (!this.allowCaching) {
                    this.rndKey = Math.floor(Math.random() * 10000).toString()
                }
                options.mosaicRule = new MosaicRule({
                    where: "step = '" + this.step + "' and " + this.rndKey + "=" + this.rndKey
                })
            }

            this.esriLayer = new ImageryLayer(options);

            
        } else if (this.layerType == "FeatureLayer") {
            this.esriLayer = new FeatureLayer(options)

        } else if (this.layerType == "WMSLayer") {
            this.esriLayer = new WMSLayer(options)
        } else if (this.layerType == "WMSTimeLayer") {
            this.esriLayer = new WMSLayer(options)

            if(this.timeSteps != null) {
                if((this.esriLayer as WMSLayer).customLayerParameters == null) {
                    (this.esriLayer as WMSLayer).customLayerParameters = {}
                }
    
                (this.esriLayer as WMSLayer).customLayerParameters['time'] = `${this.timeSteps[(+this.step-1900)]}`;
            }
            
        } else if (this.layerType == "WMTSLayer") {
            this.esriLayer = new WMTSLayer(options)
        } else if (this.layerType == "WCSLayer") {
            this.esriLayer = new WCSLayer(options)

            if(this.timeSteps != null) {
                if((this.esriLayer as WCSLayer).customParameters == null) {
                    (this.esriLayer as WCSLayer).customParameters = {}
                }
                (this.esriLayer as WCSLayer).customParameters['time'] = `${this.timeSteps[(+this.step-1900)]}`;
            }
            
        } else if (this.layerType == "MapImageLayer") {
            this.esriLayer = new MapImageLayer(options)
        }

        if (this.esriLayer && this.onLoadCallback) {
            this.onLoadCallback()
        }
    }

    /**
     *  Adds layer to the provided map, or the activeView's map if none provided
     */
    addLayerToMap(map: Map | undefined = appConfig.activeView?.map){
        if(!map) return;
        if(!this.esriLayer){
            this.initESRILayer()
        }
        if(!this.esriLayer) return;

        map.add(this.esriLayer, this.order)
        this.attachedToMap = true
    }

    /**
     * Ensures the layers order is represented in the current Map
     */
    fixOrder(map: Map | undefined = appConfig.activeView?.map) {
        if(!map) return;
        if(!this.esriLayer) return;
        map.reorder(this.esriLayer, this.order)
    }

    /**
     *  Remove layer from provided Map
     */
    removeLayerFromMap(map: Map | undefined = appConfig.activeView?.map){
        if(!map) return;
        if(!this.esriLayer) return;
        map.remove(this.esriLayer)
        this.attachedToMap = false
    }


    /**
     * Removes all metadata and generates a new key in order to prevent using any cached images 
     */
    invalidateCache(meta_only: boolean = false) {
        if (!meta_only) {
            this.rndKey = Math.floor(Math.random() * 10000).toString()
        }
        this.metadata = {}
    }

    /**
     * Run an identify task. Will only work for an ImageService 
     * TODO: Check this will work with Geoserver and adapt if needed
     */
    identify(lat: number, lon: number, step: string = this.step): Promise<any> {
        return new Promise((resolve, reject) => {

            if(!this.url) {
                reject(); 
                return;
            }

            if(this.layerType === 'WMSTimeLayer' || this.layerType === 'WMSLayer') {

                if(this.layerId == null) return;

                const params = new URLSearchParams({
                    service: 'WMS',
                    version: '1.1.0',
                    request: 'GetFeatureInfo',
                    layers: this.layerId,
                    query_layers: this.layerId,
                    bbox: [
                        ...[lon, lat].map((x) => x - 0.01),
                        ...[lon, lat].map((x) => x + 0.01),
                    ].join(','),
                    srs: 'EPSG:4326',
                    width: '10',
                    height: '10',
                    x: '5',
                    y: '5',
                    info_format: 'text/xml',
                    feature_count: '50',
                });

                if (step != null && this.timeSteps != null) params.append('TIME', this.timeSteps[+step-1]);

                fetch(`${this.url}?${params.toString()}`).then((resp) => resp.json()).then(results => {
                    resolve(results)
                })


            } else if(this.layerType === "WCSLayer") {
                if((this.esriLayer as WCSLayer).rasterInfo == null) {
                    (this.esriLayer as WCSLayer).fetchPixels(new Extent({
                        xmin: lon-0.01,
                        ymin: lat-0.01,
                        xmax: lon+0.01,
                        ymax: lat+0.01,
                    }), 1, 1, {
                        interpolation: 'nearest',
                    }).then((results) => {
                        console.log('fetchPixels results')
                        console.log(results)
                        resolve(results)
                    }, (e) => {
                        console.log("Failed to do identify")
                        reject(e)
                    })
                } else {
                    (this.esriLayer as WCSLayer).identify(new Point({
                        latitude: lat,
                        longitude: lon
                    }), {
                        multidimensionalDefinition: this.timeSteps && this.step != null ? [new DimensionalDefinition({
                            variableName: "timestep", 
                            dimensionName: "TIME",
                            values: [new Date(this.timeSteps[(+this.step-1900)]).getTime()]
                        })] : []
                    }).then((results) => {
                        resolve(results)
                    }, (e) => {
                        console.log("Failed to do identify")
                        reject(e)
                    })
                }
                
            } else {
                let idfyParams = new ImageIdentifyParameters()
                idfyParams.geometry = new Point({
                    latitude: lat,
                    longitude: lon
                })
    
                idfyParams.mosaicRule = new MosaicRule({
                    where: "step = '" + this.step + "' and " + this.rndKey + "=" + this.rndKey
                })
    
                imageService.identify(this.url, idfyParams).then((results) => {
                    resolve(results)
                }, (e) => {
                    console.log("Failed to do identify")
                    reject(e)
                })
            }
        })
    }

    /**
     *  Sets to current time step and refreshes the ESRI Layer
     */
    setStep(newStep: string){
        this.step = newStep;

        if(!this.allowCaching){
            this.rndKey =   Math.floor(Math.random()*10000).toString()
        }
        
        if(this.layerType=="ImageryLayer"){
            (this.esriLayer as ImageryLayer).mosaicRule.where = "step = '"+ this.step +"' and "+ this.rndKey +"="+ this.rndKey;
            

            (this.esriLayer as ImageryLayer).refresh()
        } if (this.layerType == "MapImageLayer") {
            // @ts-ignore
            (this.esriLayer as MapImageLayer).sublayers.items[0].definitionExpression = "step = '" + this.step + "' and " + this.rndKey + "=" + this.rndKey;

            (this.esriLayer as MapImageLayer).refresh()
        } else if(this.layerType == "WMSTimeLayer" && this.timeSteps != null) {

            (this.esriLayer as WMSLayer).customLayerParameters = {
                time: `${this.timeSteps[(+this.step-1900)]}`
            };
            
            (this.esriLayer as WMSLayer).refresh()

        } else if(this.layerType == "WCSLayer" && this.timeSteps != null) {

            const newTime = this.timeSteps[(+this.step-1900)];

            if(this.esriLayer) {
                const newLayer = new WCSLayer({
                    visible: this.esriLayer.visible,
                    url: (this.esriLayer as WCSLayer).url,
                    customParameters: {
                        ...(this.esriLayer as WCSLayer).customParameters,
                        time: newTime,
                    },
                    // @ts-ignore
                    renderer: this.esriLayer.renderer.clone(),
                    bandIds: (this.esriLayer as WCSLayer).bandIds,
                    version: (this.esriLayer as WCSLayer).version,
                    coverageId: (this.esriLayer as WCSLayer).coverageId,
                    multidimensionalDefinition: (this.esriLayer as WCSLayer).multidimensionalDefinition.map(m => m.clone()),
                });

                const oldLayer = this.esriLayer;
                this.esriLayer = newLayer;

                appConfig.activeView?.map.add(this.esriLayer,this.order)

                const handle = watchUtils.when(this.esriLayer, 'loadStatus', (val) => {
                    if(val !== 'loaded') return;

                    appConfig.activeView?.map.remove(oldLayer)
                    oldLayer.destroy();
                    handle.remove();
                })

            }

            // (this.esriLayer as WCSLayer).customParameters = {
            //     time: `${newTime}`
            // };


            // (this.esriLayer as WCSLayer).timeExtent = new TimeExtent({
            //     start: new Date(newTime),
            //     end: new Date(newTime),
            // });

            // (this.esriLayer as WCSLayer).timeInfo = new TimeInfo({
            //     interval: {
            //         value: 10,
            //         unit: 'minutes',
            //     },
            //     fullTimeExtent: new TimeExtent({
            //         start: new Date(this.timeSteps[0]),
            //         end: new Date(this.timeSteps[this.timeSteps.length-1]),
            //     }),
            // });

            // // @ts-ignore
            // (this.esriLayer as WCSLayer).tileInfo = (this.esriLayer as WCSLayer).tileInfo.clone();
            
            
            // (this.esriLayer as WCSLayer).multidimensionalDefinition = [
            //     // @ts-ignore
            //     {
            //         variableName: "timestep", 
            //         dimensionName: "TIME",
            //         // @ts-ignore
            //         values: [new Date(newTime).getTime()]
            //     }
            // ];

            // @ts-ignore
            // (this.esriLayer as WCSLayer).refreshTimestamp = new Date(newTime).getTime() - 60*60*24;

            // // @ts-ignore
            // this.esriLayer.renderer = (this.esriLayer as WCSLayer).renderer.clone();

            // @ts-ignore
            (this.esriLayer as WCSLayer).refresh();
        }
    }

    /**
     *  Returns the layers metadata, including it's timestamp from the cache if exists, 
     *  otherwise it will load it from the service
     */
    getTimeMetaData(step: string = this.step) : Promise<any> {
        return new Promise( (resolve, reject) => {
            if(!this.metadata[step]){
                this._requestTimeMetadata(step).then((results) => {
                    this.metadata[step] = results
                    resolve(this.metadata[step])
                }, (e) => {
                    reject(e)
                })
            } else {
                resolve(this.metadata[step])
            }
        })
        
    }

    /**
     *  Returns the layers metadata by exclusively querying the service
     */
    _requestTimeMetadata(step: string = this.step) : Promise<any> {
        return new Promise( (resolve, reject) => {
            if((this.layerType === 'WMSTimeLayer' || this.layerType === 'WCSLayer') && this.boundaryLayerId) {
                const params = new URLSearchParams({
                    'SERVICE': 'WMS',
                    'VERSION': '1.1.1',
                    'REQUEST': 'GetFeatureInfo',
                    'FORMAT': 'image/png',
                    'TRANSPARENT': 'true',
                    'QUERY_LAYERS': this.boundaryLayerId,
                    'LAYERS': this.boundaryLayerId,
                    'INFO_FORMAT': 'application/json',
                    'exceptions': 'application/json',
                    'FEATURE_COUNT': '500',
                    'X': '50',
                    'Y': '50',
                    'SRS': 'EPSG:4326',
                    'WIDTH': '101',
                    'HEIGHT': '101',
                    'BBOX': '73.828125,-97.734375,215.859375,44.296875'
                });

                // if (step) {
                //     params.append('cql_filter', `image_tag_date = ${(+step-1900)+1}`)
                // }

                $.getJSON(`${this.url}?${params.toString()}`).then((results) => {
                    console.log(`Querying layer data with step ${(+step-1900)+1}`)

                    // process_datetime

                    const features: any[] = results.features;

                    features.sort((a,b) => {
                        // Get smallest date first in list
                        // this way we can treat step as the index
                        // of the list
                        const aDate = new Date(a.properties.image_tag_date);
                        const bDate = new Date(b.properties.image_tag_date);

                        if(aDate < bDate) return -1;
                        if(aDate > bDate) return 1;
                        return 0;
                    })

                    const seen = {};

                    const sortedFilteredFeatures = features.filter((f) => {
                        if(seen[f.properties.image_tag_date] != null) 
                            return false
                        
                        seen[f.properties.image_tag_date] = true;
                        return true;
                    });

                    this.timeSteps = sortedFilteredFeatures.map((f) => f.properties.image_tag_date)

                    resolve({
                        labels: this.timeSteps,
                        process_datetime: sortedFilteredFeatures?.[(+step-1900)]?.properties?.image_tag_date
                    })
                })

            } else {
                let rndQryKey =   Math.floor(Math.random()*1000).toString()
        
                let qry = new Query()
                qry.returnGeometry = false
                qry.outFields = ["*"]
                qry.where = "step = '"+ step +"' and "+ rndQryKey +"="+ rndQryKey

                if(!this.qryUrl) return;

                query.executeQueryJSON(this.qryUrl, qry).then( (results) => {
                    if(results.features && results.features.length > 0){
                        resolve(results.features[0].attributes)
                    } else {
                        console.log("No Features returned from query")
                        console.log(results)
                        reject("No query feature results")
                    }
                    
                }, (e) => {
                    console.log("Failed to get metadata")
                    reject(e)
                })
            }
        })
    }

    setOnSelect(callback: typeof this.onSelectCallback) {
        this.onSelectCallback = callback
    }

    onSelect(e?: any) {
        if (this.warnings && !this.warningShown) {
            showWarning('Warning: ', this.warnings)
            this.warningShown = true
        }
        if(this.onSelectCallback){
            this.onSelectCallback(e)
        }
        
    }

    setOnUpdate(callback: typeof this.onUpdateCallback) {
        this.onUpdateCallback = callback
    }

    onUpdate(e?: any){
        if(this.onUpdateCallback){
            this.onUpdateCallback(e)
        }
    }

}

export function getCustomBaseMaps(id: string){
    if(!customBaseMaps){
        initCustomBaseMaps()
    }
    return customBaseMaps?.[id]
}

export function getLayers() : Layers | null {
    if(!layersCache){
        initLayers()
    }
    return layersCache
}

export function getLayer(id?: string | null) : Layer | null {
    if(!layersCache){
        initLayers()
    }
    if(!id) return null;
    return layersCache?.[id] ?? null
}

/*
 *  Returns all layers given layer[attr] == val
 */
export function getLayerIdsWhere(attr: string, val: any){
    // returns the keys of the layers as to not cause copies
    if(!layersCache){
        initLayers()
    }
    let results : string[] = []
    for(let key in layersCache){
        if(layersCache[key][attr]==val){
            results.push(key)
        }
    }
    return results
}


function initCustomBaseMaps(){

    customBaseMaps = {}

    customBaseMaps['lpibasemap'] = new Basemap({
        baseLayers: [new MapImageLayer({ url: "https://maps.six.nsw.gov.au/arcgis/rest/services/public/NSW_Base_Map/MapServer" })],
        title: "LPI Basemap",
        id: "lpibasemap",
        thumbnailUrl: "https://www.example.com/images/thumbnail_2014-11-25_61051.png"
      })

    customBaseMaps['lpiimagery'] = new Basemap({
        baseLayers: [new MapImageLayer({ url: "https://maps.six.nsw.gov.au/arcgis/rest/services/public/NSW_Imagery/MapServer" })],
        title: "LPI Imagery",
        id: "lpiimagery",
        thumbnailUrl: "https://www.example.com/images/thumbnail_2014-11-25_61051.png"
    })
}


function band_to_col(pixelData: any) {
    // This is literally a do nothing function
    // Basically tells the imageservice to use each band as is
    // Without this, the imageserver will return a grey box
}


async function get_planet_mosaics(): Promise<string[][]> {
    return new Promise((resolve, reject) => {
        fetch("../proxy/proxy.ashx?https://api.planet.com/basemaps/v1/mosaics").then((response) => {
            response.json().then((data) => {
                let mosaics: string[][] = []
                let splitName = []
                for (let i = data.mosaics.length - 1; i >= 0 && i >= data.mosaics.length - 24; i--) {
                    splitName = data.mosaics[i].name.split('_')
                    mosaics.push([data.mosaics[i].name, monthOfYear(parseInt(splitName[3])) + ' ' + splitName[2]])
                }
                resolve(mosaics)
            })
        }).catch((reason) => {
            console.log('Error retrieving Planet API Mosaics List: ' + reason);
            reject(reason)
        })
    })
}

export function get_planet_explorer_link(point: Point, zoom: number = 13): string {
    try {
        let mosaic_id = planetMosaics[0][0]
        return "https://planet.com/explorer/#/mosaic/" + mosaic_id + "/center/" + point.longitude.toString() + "," + point.latitude.toString() + "/zoom/" + zoom + "/"
    } catch (e) {
        return "https://planet.com/explorer/#/" + "/center/" + point.longitude.toString() + "," + point.latitude.toString() + "/zoom/" + zoom + "/"
    }
    
}

export function get_latest_planet_image_time(point: Point): Promise<string> {
    return new Promise((resolve, reject) => {



        resolve("")
    })
}

// Main init layers function, will load all layers for this application.
export function initLayers(map?: Map): Promise<Layers> {
    return new Promise(async (resolve, reject) => {
        layersCache = {}

        if (!map && appConfig.activeView && appConfig.activeView.map) {
            map = appConfig.activeView.map
        }
        await loadIncidents()
        layersCache["Himawari_MaskedTemp"] = initHimawari_Masked()

        layersCache["Himawari_Band7"] = initHimawari_YellowMask()

        layersCache["Himawari_Composite1km"] = new Layer({
            id: "Himawari_Composite1km",
            isContextual: false,
            name: "Day & Night (1km)",
            layerType: "WMSTimeLayer",
            order: 11,
            url: AIG_GEOSERVER_URL,
            qryUrl: AIG_GEOSERVER_URL,
            boundaryLayerId: 'aig:himawari_ide00437_boundary', // This should be 00435 but the time steps seem to be wrong
            layerId: 'aig:himawari_ide00435_t',
            desc: "Post-Processed Enhanced True Colour RGB & Night Infrared  Himawari Satellite Imagery",
            esriLayerOptions: {
                sublayers: [
                    { 
                        name: 'aig:himawari_ide00435_t'
                    },
                ],
            },
        })


        layersCache["Himawari_Zehr"] = new Layer({
            id: "Himawari_Zehr",
            isContextual: false,
            name: "Cloud Top Temperature (2km)",
            layerType: "WMSTimeLayer",
            order: 12,
            url: AIG_GEOSERVER_URL,
            qryUrl: AIG_GEOSERVER_URL,
            boundaryLayerId: 'aig:himawari_ide00433_boundary',
            desc: "Post-Processed Infrared & Zehr False Colour Enhancement Himawari Satellite Imagery",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: 'aig:himawari_ide00433_t',
                        popupEnabled: true,
                        queryable: true,
                    },
                ],
            },
        })

        layersCache["Himawari_WaterVapour"] = new Layer({
            id: "Himawari_WaterVapour",
            isContextual: false,
            name: "Water Vapour (All Levels, 2km)",
            layerType: "WMSTimeLayer",
            order: 13,
            url: AIG_GEOSERVER_URL,
            qryUrl: AIG_GEOSERVER_URL,
            boundaryLayerId: 'aig:himawari_ide00437_boundary',
            desc: "Post-Processed Band 8 False Colour Enhanced Himawari Satellite Imagery",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: 'aig:himawari_ide00437_t',
                        popupEnabled: true,
                        queryable: true,
                    },
                ],
            },
        })


        // await loadIncidents()

        layersCache["IconFireFeatures"] = new Layer({
            id: "IconFireFeatures",
            isContextual: true,
            name: "ICON Fire Features",
            layerType: "MapImageLayer",
            url: GIS_MAP_ARCGIS_SERVER + "/Operations/IconFireFeatures/MapServer",
            desc: "Mapped Fire Features",
            esriLayerOptions: {
            },
            controls: [],
        })

        layersCache["IDZ20010_wind"] = new Layer({
            id: "IDZ20010_wind",
            isContextual: true,
            name: "Surface Obs, Wind",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "BoM AWS Surface Wind Observations  (knots, degN)",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20010_wind"
                    }
                ]
            }
        })

        layersCache["IDR00010_rainrate"] = new Layer({
            id: "IDR00010_rainrate",
            isContextual: true,
            name: "Radar Rain Rate and Storm Tracking",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Radar Reflectivity Rain Rate (mm/hr) and Radar Thunderstorm Tracking",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDR00011_track"
                    },
                    {
                        name: "IDR00011"
                    },
                    {
                        name: "IDR00010"
                    },
                ]
            }
        })

        layersCache["Lightning"] = new Layer({
            id: "Lightning",
            isContextual: true,
            name: "Lightning Strikes",
            layerType: "MapImageLayer",
            url: GIS_MAP_ARCGIS_SERVER + "Reference/Lightning/MapServer",
            desc: "Lightning strikes over the past 48 hours",
            esriLayerOptions: {
                sublayers: [
                    {
                        id: 0,
                        visible: true,
                    }
                ]
            },
            controls: [],
        })

        layersCache["IDZ20010_rainfall_9am"] = new Layer({
            id: "IDZ20010_rainfall_9am",
            isContextual: true,
            name: "Surface Obs, Rain since 9am",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Surface Observations, Rain since 9am (mm)",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20010_rainfall_9am"
                    }
                ]
            }
        })
        
        layersCache["google_early_detection_layer"] = new Layer({
            id: "google_early_detection_layer",
            isContextual: true,
            name: "Google Early Fire Detection",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: " Early Fire Detection",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "google_fire_detection_24hrs"
                    }
                ]
            }
        })

        layersCache["IDZ20010_weatherobs"] = new Layer({
            id: "IDZ20010_weatherobs",
            isContextual: true,
            name: "All Surface Weather Obs",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "BoM AWS Surface Wind (knots, degN), Pressure (hPa), Air Temp. (°C), Relative Humidity (%) and Rain since 9am (mm)",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20010_wind"
                    },
                    {
                        name: "IDZ20010_pres"
                    },
                    {
                        name: "IDZ20010_air_temperature"
                    },
                    {
                        name: "IDZ20010_rel_humidity"
                    },
                    {
                        name: "IDZ20010_rainfall_9am"
                    }
                ]
            }
        })

        layersCache["IDZ20005_6"] = new Layer({
            id: "IDZ20005_6",
            isContextual: true,
            name: "Severe Weather Warnings",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "(Land) Severe Weather and Thunderstorm Warnings",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20005",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20006",
                        popupEnabled: true,
                        queryable: true,
                    }
                ]
            }
        })

        // Not longer have planet API Key, uncomment if 
        // one is aquired
        // layersCache["Planet_Mosaic"] = new Layer({
        //     id: "Planet_Mosaic",
        //     isContextual: true,
        //     name: "Planet Mosaics",
        //     order: 4,
        //     layerType: "WMTSLayer",
        //     url: "https://api.planet.com/basemaps/v1/mosaics/wmts",
        //     desc: "Monthly Mosaics generated from the Planet API",
        //     allowRefresh: false,
        //     esriLayerOptions: {
        //         visible: false,
        //     },
        //     controls: [
        //         {
        //             id: 'sublayerselector-control',
        //             type: "SublayerselectorWidget",
        //             settings: {
        //                 queryFunction: get_planet_mosaics,
        //             }
        //         }
        //     ],
        // })

        // layersCache["Planet_Mosaic_2"] = new Layer({
        //     id: "Planet_Mosaic_2",
        //     isContextual: true,
        //     name: "Comparison Planet Mosaics",
        //     order: 3,
        //     layerType: "WMTSLayer",
        //     url: "https://api.planet.com/basemaps/v1/mosaics/wmts",
        //     desc: "A copy of the Planet Mosaic Layer above, intended to have a different Mosaic selected allowing the opacity control to be used as a fade between the different mosaics.",
        //     allowRefresh: false,
        //     esriLayerOptions: {
        //         visible: false,
        //     },
        //     controls: [
        //         {
        //             id: 'sublayerselector-control',
        //             type: "SublayerselectorWidget",
        //             settings: {
        //                 queryFunction: get_planet_mosaics,
        //             }
        //         }
        //     ],
        // })

        layersCache["IDZ20012000"] = new Layer({
            id: "IDZ20012000",
            isContextual: true,
            name: "Today's Fire Weather Warnings",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Today's National Fire Danger Ratings",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20012000",
                        popupEnabled: true,
                    }
                ]
            }
        })

        layersCache["IDZ20012001"] = new Layer({
            id: "IDZ20012001",
            isContextual: true,
            name: "Tomorrow's Fire Weather Warnings",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Tomorrow's Forecast National Fire Danger Ratings",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20012001",
                        popupEnabled: true,
                    }
                ]
            }
        })

        layersCache["IDY10012_day0"] = new Layer({
            id: "IDY10012_day0",
            isContextual: true,
            name: "Heatwave Forecast",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Heatwave Forecast (Days 0 to +2) ",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDY10012_day0",
                    },
                ],
                opacity: 0.4
            }
        })

        layersCache["Flood_watch"] = new Layer({
            id: "Flood_watch",
            isContextual: true,
            name: "Flood & Marine Warnings",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Flood Warnings, Watch Areas, Marine Wind Warnings (AUS) and Hazardous Surf Warnings (NSW & QLD)",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20017000",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20008000",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20016_label",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20016",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20013",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20013_label",
                        popupEnabled: true,
                        queryable: true,
                    },
                ],
                opacity: 1,
            }
        })


        layersCache["River_Obs"] = new Layer({
            id: "River_Obs",
            isContextual: true,
            name: "River Observations",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "River Observations, Flood levels and Tendency (AUS)",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDN62011_flood",
                    },
                    {
                        name: "IDN62011_all",
                    },
                    {
                        name: "IDN62011_wtr_lvl",
                    },
                    {
                        name: "IDN62011_stn_name",
                    },
                    
                    {
                        name: "IDD60280_flood",
                    },
                    {
                        name: "IDD60280_all",
                    },
                    {
                        name: "IDD60280_wtr_lvl",
                    },
                    {
                        name: "IDD60280_stn_name",
                    },

                    {
                        name: "IDQ65470_flood",
                    },
                    {
                        name: "IDQ65470_all",
                    },
                    {
                        name: "IDQ65470_wtr_lvl",
                    },
                    {
                        name: "IDQ65470_stn_name",
                    },
                    {
                        name: "IDS65178_flood",
                    },
                    {
                        name: "IDS65178_all",
                    },
                    {
                        name: "IDS65178_wtr_lvl",
                    },
                    {
                        name: "IDS65178_stn_name",
                    },

                    {
                        name: "IDT65010_flood",
                    },
                    {
                        name: "IDT65010_all",
                    },
                    {
                        name: "IDT65010_wtr_lvl",
                    },
                    {
                        name: "IDT65010_stn_name",
                    },
                    {
                        name: "IDV65201_flood",
                    },
                    {
                        name: "IDV65201_all",
                    },
                    {
                        name: "IDV65201_wtr_lvl",
                    },
                    {
                        name: "IDV65201_stn_name",
                    },

                    {
                        name: "IDW60411_flood",
                    },
                    {
                        name: "IDW60411_all",
                    },
                    {
                        name: "IDW60411_wtr_lvl",
                    },
                    {
                        name: "IDW60411_stn_name",
                    },
                    
                ],
            }
        })

        layersCache["cyclone_tsunami"] = new Layer({
            id: "cyclone_tsunami",
            isContextual: true,
            name: "Cyclone and Tsunami Warnings",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Cyclone Locations, Fix (UTC Time), Track and Threat Areas. Tsunami Warnings (AUS and Offshore Territories)",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDZ20002_info",
                    },
                    {
                        name: "IDZ20002",
                        popupEnabled: true,
                        queryable: true,
                    },
                    {
                        name: "IDZ20009_name",
                    },
                    {
                        name: "IDZ20009_trackarea",
                    },
                    {
                        name: "IDZ20009_track",
                    },
                    {
                        name: "IDZ20009_fix",
                    },
                    {
                        name: "IDZ20009_windarea",
                    },
                    {
                        name: "IDZ20009_threatarea"
                    }
                ],
                opacity: 0.6
            }
        })

        layersCache["Sentinel_Hotspot"] = new Layer({
            id: "Sentinel_Hotspot",
            isContextual: true,
            name: "Sentinel Hotspots",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "GeoScience Australia Sentinel Hotspots WMS Layer",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "fireweather:hotspots_three_days"
                    }
                ]
            }
        })

        layersCache["FireManagementArea"] = new Layer({
            id: "FireManagementArea",
            isContextual: true,
            name: "Fire Management Areas",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "All Fire Management Areas",
            esriLayerOptions: {
                sublayers: [{
                    name: "	fireweather:fire_management_area",
                    popupEnabled: true,
                    queryable: true,
                }]
            },
            controls: [
                {
                    id: 'query-control',
                    type: "QueryWidget",
                    settings: {
                        searchField: 'fire_area_name',
                        max: 10,
                        goToParams: {
                            zoom: 8
                        }
                    }
                }
            ],
        })

        layersCache["FireWeatherAus"] = new Layer({
            id: "FireWeatherAus",
            isContextual: true,
            name: "Fire Weather Areas Australia",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Displays Fire Weather Areas across Australia",
            layerId: 'fireweather:fire_weather_area',
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "fireweather:fire_weather_area",
                        popupEnabled: true,
                        queryable: true,
                    },
                ],
            },
            controls: [
                {
                    id: 'query-control',
                    type: "QueryWidget",
                    settings: {
                        searchField: 'fire_area_name',
                        max: 10,
                        goToParams: {
                            zoom: 8
                        }
                    }
                }
            ],
        })

        // User must be on the VPN to see
        layersCache["RFS_Zone"] = new Layer({
            id: "RFS_Zone",
            isContextual: true,
            name: "RFS Districts",
            layerType: "MapImageLayer",
            url: GIS_MAP_ARCGIS_SERVER + "/Reference/BaseDynamicData/MapServer",
            qryUrl: GIS_MAP_ARCGIS_SERVER + "/Reference/BaseDynamicData/MapServer/26",
            desc: "All RFS Districts. If not loading, please connect to the VPN",
            esriLayerOptions: {
                sublayers: [{
                    id: 26,
                    visible: true,
                }
                ]
            },
            controls: [
                {
                    id: 'query-control',
                    type: "QueryWidget",
                    settings: {
                        searchField: 'ZONETEAM',
                        max: 10,
                        goToParams: {
                            zoom: 10
                        }
                    }
                }
            ],
        })

        // User must be on the VPN to see
        layersCache["National_Park"] = new Layer({
            id: "National_Park",
            isContextual: true,
            name: "National Parks",
            layerType: "MapImageLayer",
            url: GIS_MAP_ARCGIS_SERVER + "/Reference/BaseDynamicData/MapServer",
            qryUrl: GIS_MAP_ARCGIS_SERVER + "/Reference/BaseDynamicData/MapServer/18",
            desc: "NSW National Parks. If not loading, please connect to the VPN",
            esriLayerOptions: {
                sublayers: [{
                    id: 18,
                    visible: true,
                }
                ]
            },
            controls: [
                {
                    id: 'query-control',
                    type: "QueryWidget",
                    settings: {
                        searchField: 'NAME',
                        max: 10,
                        goToParams: {
                            zoom: 9
                        }
                    }
                }
            ],
        })

        // User must be on the VPN to see
        layersCache["State_Forest"] = new Layer({
            id: "State_Forest",
            isContextual: true,
            name: "State Forests",
            layerType: "MapImageLayer",
            url: GIS_MAP_ARCGIS_SERVER + "/Reference/BaseDynamicData/MapServer",
            qryUrl: GIS_MAP_ARCGIS_SERVER + "/Reference/BaseDynamicData/MapServer/14",
            desc: "NSW State Forests. If not loading, please connect to the VPN",
            esriLayerOptions: {
                sublayers: [{
                    id: 14,
                    visible: true,
                }
                ]
            },
            controls: [
                {
                    id: 'query-control',
                    type: "QueryWidget",
                    settings: {
                        searchField: 'STATEFORESTNAME',
                        max: 10,
                        goToParams: {
                            zoom: 9
                        }
                    }
                }
            ],
        })


        layersCache["Jurisdictions"] = new Layer({
            id: "Jurisdictions",
            isContextual: true,
            name: "Jurisdictions",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Displays the Jurisdiction Borders",
            esriLayerOptions: {
                sublayers: [{
                    name: "fireweather:jurisdiction",
                    popupEnabled: true,
                    queryable: true,
                }]
            },
        })

        layersCache["IDR00006_coverage"] = new Layer({
            id: "IDR00006_coverage",
            isContextual: true,
            name: "Radar Coverage",
            layerType: "WMSLayer",
            url: GEOSERVER_URL,
            desc: "Optimum Radar Extent",
            esriLayerOptions: {
                sublayers: [
                    {
                        name: "IDR00006"
                    },
                ]
            }
        })

        let tmpOrderedKeys = []

        if (map) {
            for (let key in layersCache) {
                tmpOrderedKeys.push({ key: key, order: layersCache[key].order })
            }

            tmpOrderedKeys.sort((a, b) => {
                return a.order - b.order
            })

            for (let keyorder of tmpOrderedKeys) {
                const layer = layersCache[keyorder.key];
                layer.addLayerToMap(map)
                if(layer.esriLayer)
                    layer.esriLayer.visible = false
            }
            /*
            for (let key in layersCache) {
                layersCache[key].fixOrder(map)
            }
            */
        }

        resolve(layersCache)
    })
}


function initHimawari_YellowMask(): Layer {
    let id = "Himawari_Band7"

    const layerOptions: __esri.WCSLayerProperties = {
        url: AIG_GEOSERVER_URL,
        customParameters: {},
        renderer: {
            type: "raster-stretch",
            stretchType: "min-max",
            statistics: [
                {
                    min: 273,
                    max: 80+273,
                    avg: 1,
                    stdev: 1
                }, 
                {
                    min: 80+273,
                    max: 120+273,
                    avg: 1,
                    stdev: 1
                }
            ],
            colorRamp: {
                type: "multipart",
                colorRamps: [
                    {
                        fromColor: [255, 255, 255],
                        toColor: [0, 0, 0]
                    },
                    {
                        fromColor: [255, 255, 0],
                        toColor: [255, 0, 0]
                    }
                ]
            }
        },
        bandIds: [0],
        version: "1.0.0",
        coverageId: "aig:himawari_ide00207_t",
        multidimensionalDefinition: [
            {
                variableName: "timestep", 
                dimensionName: "TIME",
                values: [0]
            }
        ]
    }

    let layer = new Layer({
        id: id,
        timeExtent: tx10min2hours,
        params: {
            minTemp: -20,
            maxTemp: 60,
            maskTemp: 40,
            lowColor: [255, 255, 255],
            highColor: [0, 0, 0],
            aboveStartColor: [255, 255, 0],
            aboveEndColor: [255, 0, 0],
        },
        isContextual: false,
        name: "Surface Temperature (2km)",
        layerType: 'WCSLayer',
        url: AIG_GEOSERVER_URL,
        qryUrl: AIG_GEOSERVER_URL,
        desc: "Raw Band7 Near Infrared 3.9µm Himawari Satellite Imagery.",
        order: 10,
        esriLayerOptions: layerOptions,
        boundaryLayerId: 'aig:himawari_ide00207_boundary',
        controls: [
            {
                id: 'slider-control',
                type: "ValueSlider",
                settings: {
                    min: 0,
                    max: 120,
                    step: 0.25,
                    value: 40,
                    labels: ['0°C', '120°C'],
                    pretext: "Cutoff Temperature ",
                    posttext: "° C",
                    slideField: ['params', 'maskTemp']
                }
            }
        ],
        warnings: [
            "The Information displayed is raw satellite data. It must be used in conjunction with other sources of information and intelligence"
        ]
    })

    function himawari_yellow_pixelFilter(pixelData: __esri.PixelData) {
        let rasterUpdateTime = Date.now();

        if (pixelData == null || pixelData.pixelBlock == null) {
            return;
        }

        let pixelBlock = pixelData.pixelBlock;
        let pixels = pixelBlock.pixels;
        let mask = pixelBlock.mask;
        let numPixels = pixelBlock.width * pixelBlock.height;
        if (pixels == null) {
            return;
        }

        let pTemp = pixels[0];
        let pr = new Uint8Array(pTemp.length); //set up array for red values
        let pg = new Uint8Array(pTemp.length); //set up array for green values
        let pb = new Uint8Array(pTemp.length); //set up array for blue values

        if (mask == null) {
            mask = new Uint8Array(pTemp.length);   //mask = new Uint8Array(pTemp.length);
            pixelBlock.mask = mask;
        }
        let p = 0;

        const layer = layersCache?.[id];

        if(!layer) return;

        for (let i = 0; i < numPixels; i++) {
            mask[i] = pTemp[i] > 0 ? 1 : 0;

            //apply color based on temperature value of each pixel
            if (mask[i]) {
                if (pTemp[i] - 273.15 >= Math.floor(layer.params.maskTemp)) {
                    // Temperature is above slider, make the color 'aboveColor'

                    p = ((pTemp[i] - 273.15) - layer.params.maskTemp) / layer.params.maxTemp
                    if (p <= 0.0) {
                        pr[i] = layer.params.aboveStartColor[0]; // red
                        pg[i] = layer.params.aboveStartColor[1]; // green
                        pb[i] = layer.params.aboveStartColor[2]; // blue
                    } else if (p < 1.0) {
                        pr[i] = layer.params.aboveStartColor[0] + (layer.params.aboveEndColor[0] - layer.params.aboveStartColor[0]) * p; // red
                        pg[i] = layer.params.aboveStartColor[1] + (layer.params.aboveEndColor[1] - layer.params.aboveStartColor[1]) * p; // green
                        pb[i] = layer.params.aboveStartColor[2] + (layer.params.aboveEndColor[2] - layer.params.aboveStartColor[2]) * p; // blue
                    } else {
                        pr[i] = layer.params.aboveEndColor[0]; // red
                        pg[i] = layer.params.aboveEndColor[1]; // green
                        pb[i] = layer.params.aboveEndColor[2]; // blue
                    }

                } else {
                    p = ((pTemp[i] - 273.15) - layer.params.minTemp) / layer.params.maxTemp
                    if (p <= 0.0) {
                        pr[i] = layer.params.lowColor[0]; // red
                        pg[i] = layer.params.lowColor[1]; // green
                        pb[i] = layer.params.lowColor[2]; // blue
                    } else if (p < 1.0) {
                        pr[i] = layer.params.lowColor[0] + (layer.params.highColor[0] - layer.params.lowColor[0]) * p ; // red
                        pg[i] = layer.params.lowColor[1] + (layer.params.highColor[1] - layer.params.lowColor[1]) * p ; // green
                        pb[i] = layer.params.lowColor[2] + (layer.params.highColor[2] - layer.params.lowColor[2]) * p ; // blue
                    } else {
                        pr[i] = layer.params.highColor[0]; // red
                        pg[i] = layer.params.highColor[1]; // green
                        pb[i] = layer.params.highColor[2]; // blue
                    }
                }

                
            }
        }

        pixelData.pixelBlock.pixels = [pr, pg, pb];  //assign rgb values to each pixel
        pixelData.pixelBlock.statistics = [];
        pixelData.pixelBlock.pixelType = "u8";
        pixelData.pixelBlock.mask = mask;

        //console.log("Raster Update End. " + (Date.now() - rasterUpdateTime) + "ms");
    }

    layer.setOnSelect(() => {
    })

    layer.setOnUpdate(() => {
        if(!layer.esriLayer) return;

        console.log(`Updating Layer: ${layer.params.maskTemp}`)

        // @ts-ignore
        const renderer = layer.esriLayer.renderer.clone();
        
        // Unsure why maskTemp must be doubled for it to work as desired.
        renderer.statistics = [{
            min: 273,
            max: (layer.params.maskTemp*2)+273,
            avg: 1,
            stdev: 1
        }, {
            min: (layer.params.maskTemp*2)+273,
            max: 120+273,
            avg: 1,
            stdev: 1
        }];

        renderer.colorRamp = {
            type: "multipart",
            colorRamps: [
              {
                fromColor: [255, 255, 255],
                toColor: [0, 0, 0]
              },
              {
                fromColor: [255, 255, 0],
                toColor: [255, 0, 0]
              }
            ]
        }

        // @ts-ignore
        layer.esriLayer.renderer = renderer;
        
    })

    layer.initESRILayer()


    return layer
}

function initHimawari_Masked() : Layer {
    let id = "Himawari_MaskedTemp"

    const layerOptions: __esri.WCSLayerProperties = {
        url: AIG_GEOSERVER_URL,
        renderer: {
            type: "raster-stretch",
            stretchType: "min-max",
            statistics: [
                {
                    min: 80+273,
                    max: 120+273,
                    avg: 1,
                    stdev: 1
                }
            ],
            colorRamp: {
                type: "multipart",
                colorRamps: [
                    {
                        fromColor: new Color({r: 255, g: 255, b: 255, a: 0}),
                        toColor: new Color({r: 255, g: 255, b: 255, a: 0}),
                    },
                    {
                        fromColor: [255, 255, 0],
                        toColor: [255, 0, 0]
                    }
                ]
            }
        },
        bandIds: [0],
        version: "1.0.0",
        coverageId: "aig:himawari_ide00207_t",
        multidimensionalDefinition: [
            {
                variableName: "timestep", 
                dimensionName: "TIME",
                // @ts-ignore
                values: ["2022-09-18T09:00:00.000Z"]
            }
        ]
    }

    let layer = new Layer({
        id: id,
        timeExtent: tx10min2hours,
        params: {
            minTemp: 0,
            maxTemp: 80,
            maskTemp: 40,
            lowColor: [0, 0, 200],
            midColor: [20, 250, 20],
            highColor: [255, 50, 50],
        },
        isContextual: true,
        name: "Adjustable Sat. Surface Temp. (2km)",
        layerType: 'WCSLayer',
        url: AIG_GEOSERVER_URL,
        qryUrl: AIG_GEOSERVER_URL,
        boundaryLayerId: 'aig:himawari_ide00207_boundary',
        desc: "An overlay displaying satellite observed surface temperature above a specified threshold",
        // esriLayerOptions: {
        //     pixelFilter: himawari_band7_pixelFilter,
        // },
        esriLayerOptions: layerOptions,
        controls: [
            {
                id: 'slider-control',
                type: "ValueSlider",
                settings: {
                    min: 0,
                    max: 120,
                    step: 0.25,
                    value: 40,
                    labels: ['0°C', '120°C'],
                    pretext: "Cutoff Temperature ",
                    posttext: "° C",
                    slideField: ['params','maskTemp']
                }
            }
        ],
        warnings: [
            "The Information displayed is raw satellite data. It must be used in conjunction with other sources of information and intelligence"
        ]
    })

    function himawari_band7_pixelFilter(pixelData: __esri.PixelData) {
        let rasterUpdateTime = Date.now();

        if (pixelData == null || pixelData.pixelBlock == null) {
            return;
        }

        let pixelBlock = pixelData.pixelBlock;
        let pixels = pixelBlock.pixels;
        let mask = pixelBlock.mask;
        let numPixels = pixelBlock.width * pixelBlock.height;
        if (pixels == null) {
            return;
        }

        let pTemp = pixels[0];
        let pr = new Uint8Array(pTemp.length); //set up array for red values
        let pg = new Uint8Array(pTemp.length); //set up array for green values
        let pb = new Uint8Array(pTemp.length); //set up array for blue values
        
        if (mask == null) {
            mask = new Uint8Array(pTemp.length);   //mask = new Uint8Array(pTemp.length);
            pixelBlock.mask = mask;
        }
        let p = 0;

        const layer = layersCache?.[id];

        if(!layer) return;

        for (let i = 0; i < numPixels; i++) {
            mask[i] = (pTemp[i] - 273.15 >= Math.floor(layer.params.maskTemp)) ? 1 : 0;

            //apply color based on temperature value of each pixel
            if (mask[i]) {

                p = ((pTemp[i] - 273.15) - layer.params.minTemp) / layer.params.maxTemp

                if (p <= 0.0){
                    pr[i] = layer.params.lowColor[0]; // red
                    pg[i] = layer.params.lowColor[1]; // green
                    pb[i] = layer.params.lowColor[2]; // blue
                } else if(p < 0.5){
                    pr[i] = (layer.params.midColor[0] * p * 2.0) + layer.params.lowColor[0] * (0.5 - p) * 2.0; // red
                    pg[i] = (layer.params.midColor[1] * p * 2.0) + layer.params.lowColor[1] * (0.5 - p) * 2.0; // green
                    pb[i] = (layer.params.midColor[2] * p * 2.0) + layer.params.lowColor[2] * (0.5 - p) * 2.0; // blue
                } else if(p < 1.0) {
                    pr[i] = layer.params.highColor[0] * (p - 0.5) * 2.0 + layer.params.midColor[0] * (1.0 - p) * 2.0; // red
                    pg[i] = layer.params.highColor[1] * (p - 0.5) * 2.0 + layer.params.midColor[1] * (1.0 - p) * 2.0; // green
                    pb[i] = layer.params.highColor[2] * (p - 0.5) * 2.0 + layer.params.midColor[2] * (1.0 - p) * 2.0; // blue
                } else {
                    pr[i] = layer.params.highColor[0]; // red
                    pg[i] = layer.params.highColor[1]; // green
                    pb[i] = layer.params.highColor[2]; // blue
                }
            }
        }

        pixelData.pixelBlock.pixels = [pr, pg, pb];  //assign rgb values to each pixel
        pixelData.pixelBlock.statistics = [];
        pixelData.pixelBlock.pixelType = "u8";
        pixelData.pixelBlock.mask = mask;

        //console.log("Raster Update End. "+ (Date.now() - rasterUpdateTime) + "ms");
    }

    layer.setOnSelect(() => {
    })

    layer.setOnUpdate(() => {
        if(!layer.esriLayer) return;

        console.log(`Updating Layer: ${layer.params.maskTemp}`)

        // @ts-ignore
        const renderer = layer.esriLayer.renderer.clone();
        
        renderer.statistics = [{
            min: 273,
            max: (layer.params.maskTemp*2)+273,
            avg: 1,
            stdev: 1
        }, {
            min: (layer.params.maskTemp*2)+273,
            max: 120+273,
            avg: 1,
            stdev: 1
        }];

        // renderer.outputMin = layer.params.maskTemp;

        renderer.colorRamp = {
            type: "multipart",
            colorRamps: [
                {
                    fromColor: new Color({r: 255, g: 255, b: 255, a: 0}),
                    toColor: new Color({r: 255, g: 255, b: 255, a: 0}),
                },
                {
                    fromColor: [255, 255, 0],
                    toColor: [255, 0, 0]
                }
            ]
        }

        // @ts-ignore
        layer.esriLayer.renderer = renderer;
    })

    layer.initESRILayer()

    return layer
}

export function loadIncidents(reload: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {

        const fields = [
            {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid"
            },
            {
                name: "title",
                alias: "title",
                type: "string"
            },
            {
                name: "link",
                alias: "link",
                type: "string"
            },
            {
                name: "category",
                alias: "category",
                type: "string"
            },
            {
                name: "pubDate",
                alias: "pubDate",
                type: "string"
            },
            {
                name: "description",
                alias: "description",
                type: "string"
            },
            {
                name: "iconPageLink",
                alias: "iconPageLink",
                type: "string"
            },
            {
                name: "iconIntelLink",
                alias: "iconIntelLink",
                type: "string"
            },
        ];

        const pTemplate = {
            title: "{title}",
            content: [{
                type: "fields",
                fieldInfos: [
                    {
                        fieldName: "title",
                        label: "Incident",
                        visible: true
                    },
                    {
                        fieldName: "category",
                        label: "Category",
                        visible: true
                    },
                    {
                        fieldName: "pubDate",
                        label: "Publish Date",
                        visible: true
                    },
                    {
                        fieldName: "description",
                        label: "Description",
                        visible: true
                    },
                    {
                        fieldName: "iconPageLink",
                        label: "ICON Page",
                        visible: true
                    },
                    {
                        fieldName: "iconIntelLink",
                        label: "ICON Intel Link",
                        visible: true
                    },
                ]
            }]
        };

        const fillSymbol = {
            type: "simple-fill",
            color: [64, 64, 64, 1],
            style: "solid",
            outline: {
                color: "black",
                width: 0.5
            }
        };

        // Handle grabbing symbol size
        let symbolSize =22
        if (appConfig.config.layerOptions["Incidents_points"] && appConfig.config.layerOptions["Incidents_points"].params && appConfig.config.layerOptions["Incidents_points"].params.symbolSize) {
            symbolSize = appConfig.config.layerOptions["Incidents_points"].params.symbolSize
        }
        if (layersCache?.["Incidents_points"]?.params?.symbolSize) {
            symbolSize = layersCache["Incidents_points"].params.symbolSize
        }

        const renderer = {
            type: 'unique-value',
            field: 'category',
            defaultSymbol: {
                type: "picture-marker",
                url: 'img/dispatch/FireIncidentConfirmed.png',
                height: symbolSize,
                width: symbolSize
            },
            uniqueValueInfos: [
                {
                    value: 'Not Applicable',
                    symbol: {
                        type: "picture-marker",
                        url: 'img/dispatch/NotApplicable.png',
                        height: symbolSize,
                        width: symbolSize
                    }
                },
                {
                    value: 'Emergency Warning',
                    symbol: {
                        type: "picture-marker",
                        url: 'img/dispatch/Emergency_Warning.png',
                        height: symbolSize,
                        width: symbolSize
                    }
                },
                {
                    value: 'Watch and Act',
                    symbol: {
                        type: "picture-marker",
                        url: 'img/dispatch/WatchandAct.png',
                        height: symbolSize,
                        width: symbolSize
                    }
                },
                {
                    value: 'Advice',
                    symbol: {
                        type: "picture-marker",
                        url: 'img/dispatch/Advice.png',
                        height: symbolSize,
                        width: symbolSize
                    }
                }
            ]
        };

        ESRIRequest(INCIDENTS_URL, {
            responseType: "json"
        }).then((response) => {
            let pointGraphics = [];
            let polyGraphics = [];
            let point = {};
            //raw GeoJSON data
            const points = response.data.featureCollection.layers[2].featureSet
            const polygons =  response.data.featureCollection.layers[0].featureSet

            let pointObjId = 0;


            for (let i = 0; i < polygons.features.length; i++) { 
                let feature = polygons.features[i];
                //create attributes
                pointObjId++;

                let guid = feature.attributes.guid.split('/').pop()

                let attr = {
                    ObjectID: pointObjId,
                    title: feature.attributes.title,
                    link: feature.attributes.link,
                    iconPageLink: "https://icon.rfs.nsw.gov.au/FireIncident/FireIncident/Review/" + guid,
                    iconIntelLink: "https://icon.rfs.nsw.gov.au/FireIncident/FireIncident/IncidentIntel/" + guid,
                    category: feature.attributes.category,
                    pubDate: feature.attributes.pubDate,
                    description: feature.attributes.description
                };

                    //go through geometries collection looking for a point
                    let poly = new Polygon({
                        rings: feature.geometry.rings
                    });
                  
                    polyGraphics.push(new Graphic({
                        geometry: poly,
                        attributes: attr,
                        symbol: fillSymbol,
                    }));

            } 

            for (let i = 0; i < points.features.length; i++) {
                let feature = points.features[i];
                //create attributes
                pointObjId++;

                let guid = feature.attributes.guid.split('/').pop()

                let attr = {
                    ObjectID: pointObjId,
                    title: feature.attributes.title,
                    link: feature.attributes.link,
                    iconPageLink: "https://icon.rfs.nsw.gov.au/FireIncident/FireIncident/Review/" + guid,
                    iconIntelLink: "https://icon.rfs.nsw.gov.au/FireIncident/FireIncident/IncidentIntel/" + guid,
                    category: feature.attributes.category,
                    pubDate: feature.attributes.pubDate,
                    description: feature.attributes.description
                };

                //create our geometry
                point = new Point({
                    x: feature.geometry.x,
                    y: feature.geometry.y
                });

                pointGraphics.push(new Graphic({
                    geometry: point,
                    attributes: attr
                }))
            }

            let incidents_areas_layer_config = {
                source: polyGraphics,
                fields: fields,
                objectIdField: "ObjectID",
                spatialReference: {
                    wkid: 4326
                },
                renderer: {
                    type: "simple",
                    symbol: fillSymbol,
                },
                geometryType: "polygon",
                id: "incidents",
                popupTemplate: pTemplate
            }

            let incidents_points_layer_config = {
                source: pointGraphics,
                fields: fields,
                objectIdField: "ObjectID",
                renderer: renderer,
                spatialReference: {
                    wkid: 4326
                },
                geometryType: "point",
                id: "incidents",
                popupTemplate: pTemplate
            }

            if(!layersCache) return;

            let incidents_areas_layer = layersCache["Incidents_areas"]
            let incidents_points_layer = layersCache["Incidents_points"]

            if (reload && incidents_areas_layer && incidents_points_layer) {
                incidents_areas_layer.applyConfig({ esriLayerOptions: incidents_areas_layer_config }, true)
                incidents_points_layer.applyConfig({ esriLayerOptions: incidents_points_layer_config }, true)
            } else {
                layersCache["Incidents_areas"] = new Layer({
                    id: "Incidents_areas",
                    isContextual: true,
                    name: "ICON Incident Maps",
                    layerType: "FeatureLayer",
                    url: null,
                    desc: "Current Incident Polygon",
                    order: 50,
                    esriLayerOptions: incidents_areas_layer_config,
                })


                layersCache["Incidents_points"] = new Layer({
                    id: "Incidents_points",
                    isContextual: true,
                    name: "ICON Incidents",
                    layerType: "FeatureLayer",
                    url: null,
                    desc: "Incident Alert Levels, Descriptions and Details",
                    order: 100,
                    esriLayerOptions: incidents_points_layer_config,
                    params: {
                        symbolSize: 22,
                    },
                    controls: [
                        {
                            id: 'slider-control',
                            type: "ValueSlider",
                            settings: {
                                min: 12,
                                max: 24,
                                step: 1,
                                value: 22,
                                labels: ['12px', '24px'],
                                pretext: "Incident Symbol Size: ",
                                posttext: "px",
                                slideField: ['params', 'symbolSize']
                            }
                        },
                        {
                            id: 'query-control',
                            type: "QueryWidget",
                            settings: {
                                searchField: 'title',
                                max: 10,
                                goToParams: {
                                    zoom: 16
                                }
                            }
                        },
                    ],
                })

                layersCache["Incidents_points"].setOnUpdate(() => {
                    loadIncidents(true)
                });
            }

            



            resolve()
        }, reject)

    })
}